import React from 'react';
import { Lightbulb, BookOpen, MessageSquare, Heart, ExternalLink, Trash2 } from 'lucide-react';
import { usePreview } from '../../context/PreviewContext';

/**
 * Preview My Ideas Page
 * Read-only saved ideas with mock data
 */
const PreviewMyIdeasPage = () => {
  const { ideas } = usePreview();

  const getCategoryConfig = (category) => {
    switch (category) {
      case 'question':
        return { 
          icon: MessageSquare, 
          bg: 'bg-blue-50', 
          text: 'text-blue-700', 
          border: 'border-blue-200',
          label: 'Question for Doctor'
        };
      case 'research':
        return { 
          icon: BookOpen, 
          bg: 'bg-purple-50', 
          text: 'text-purple-700', 
          border: 'border-purple-200',
          label: 'Research Note'
        };
      case 'lifestyle':
        return { 
          icon: Heart, 
          bg: 'bg-green-50', 
          text: 'text-green-700', 
          border: 'border-green-200',
          label: 'Lifestyle Idea'
        };
      default:
        return { 
          icon: Lightbulb, 
          bg: 'bg-slate-50', 
          text: 'text-slate-700', 
          border: 'border-slate-200',
          label: 'Note'
        };
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">My Ideas & Research</h1>
        <p className="text-slate-500 mt-1">Ideas and notes you've saved while exploring</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">
            {ideas.filter(i => i.category === 'question').length}
          </p>
          <p className="text-sm text-blue-600">Questions</p>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-purple-700">
            {ideas.filter(i => i.category === 'research').length}
          </p>
          <p className="text-sm text-purple-600">Research</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-700">
            {ideas.filter(i => i.category === 'lifestyle').length}
          </p>
          <p className="text-sm text-green-600">Lifestyle</p>
        </div>
      </div>

      {/* Ideas List */}
      <div className="space-y-4">
        {ideas.map(idea => {
          const config = getCategoryConfig(idea.category);
          const Icon = config.icon;
          
          return (
            <div 
              key={idea.id}
              className={`bg-white rounded-xl border ${config.border} overflow-hidden`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 ${config.bg} rounded-lg`}>
                      <Icon className={`h-5 w-5 ${config.text}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{idea.title}</h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`text-xs font-medium ${config.text} ${config.bg} px-2 py-0.5 rounded-full`}>
                          {config.label}
                        </span>
                        <span className="text-xs text-slate-400">
                          Saved from {idea.source_label}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 whitespace-nowrap">
                    {new Date(idea.saved_at).toLocaleDateString('en-AU', {
                      day: '2-digit',
                      month: 'short'
                    })}
                  </span>
                </div>
                
                <p className="text-slate-600 leading-relaxed">
                  {idea.note}
                </p>
              </div>
              
              <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                <button 
                  disabled
                  className="text-sm text-slate-400 cursor-not-allowed flex items-center gap-1"
                >
                  <ExternalLink className="h-4 w-4" />
                  View Source
                </button>
                <button 
                  disabled
                  className="text-sm text-slate-400 cursor-not-allowed flex items-center gap-1"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info */}
      <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 text-center">
        <Lightbulb className="h-6 w-6 text-amber-600 mx-auto mb-2" />
        <p className="text-sm text-amber-700">
          In the full app, look for the <strong>"Save to Ideas"</strong> button on educational pages 
          to build your personal collection.
        </p>
      </div>
    </div>
  );
};

export default PreviewMyIdeasPage;
